/**
 * Парк целиком: схемы, хуки, резолв номера, зеркало машины состояний.
 *
 * Читается из `GET /v1/scout/vehicles` — отдельной ручки, а не из
 * `GET /v1/vehicles`. Разница не в доступе: скаутская проекция не отдаёт
 * `imei` (команды замку всё равно гейтятся ролью, не задачей) и, в отличие от
 * `Vehicle`, отдаёт `runtimeStale`. Без последнего фильтр «заряд < 20 %»
 * собрал бы весь парк — см. `lib/describe-bike.ts`.
 *
 * Смена статуса идёт через `/v1/vehicles/{id}/faulty|lost|in-transit|…` —
 * те же ручки, что у диспетчера, открытые роли scout по роли, без проверки
 * взятой задачи: главный полевой сценарий — «нашёл сломанный байк, на который
 * задачи нет».
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, NetworkError, request } from '@/lib/http'
import { nullAsMissing } from './nested'
import { enqueue } from '@/lib/offline/outbox'
import { findByNumber, type NumberedBike } from '@/lib/bike-number'
import type { RideZone } from '@/lib/zones'
import type { ScoutStatusAction } from '@/lib/vehicle-status'

/* ─── Транспорт ───────────────────────────────────────────────────────── */

export const labelSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  color: z.string().default(''),
  createdAt: z.string().default(''),
})
export type Label = z.infer<typeof labelSchema>

export const scoutVehicleSchema = z.object({
  id: z.string().default(''),
  qrToken: z.string().default(''),
  /**
   * C1: человеческий номер с наклейки («Aida001»). До выката BE-2 сервер
   * его не отдаёт (пустая строка) — экраны читают номер через
   * `displayNumber()`, которая подстраховывается `qrToken`, а не это поле
   * напрямую.
   */
  number: z.string().default(''),
  vehicleType: z.string().default(''),
  status: z.string().default(''),
  batteryPct: z.number().default(0),
  locked: z.boolean().default(false),
  lat: z.number().default(0),
  lng: z.number().default(0),
  lastSeenAt: z.string().default(''),
  // ⚠ ТРИ состояния, и .default() здесь ставить НЕЛЬЗЯ. На проводе это
  // optional bool: undefined — сервер про телеметрию не говорит (старый
  // бэкенд); true — снимка нет: заряд неизвестен; false — снимок свежий.
  runtimeStale: z.boolean().optional(),
  /**
   * C1: `""|"in_ride"|"faulty"|"low_battery"|"out_of_zone"|"offline"`.
   * Пустая строка НЕ означает «проблем нет» — она неотличима от «старый
   * бэкенд поля не знает». `lib/describe-bike.ts` поэтому не доверяет
   * пустому значению как окончательному ответу, а всегда досчитывает
   * эвристику по сырым полям (battery/зоны/lastSeenAt) и лишь уточняет её
   * этим полем и следующими двумя, когда они непусты.
   */
  problem: z.string().default(''),
  problemDetail: z.string().default(''),
  faultReason: z.string().default(''),
  lockedConfirmedAt: z.string().default(''),
  /** Метки диспетчера на байке. Скаут их ставит и снимает, но не создаёт и не удаляет. */
  labels: z.array(labelSchema).default([]),
  /** Чей пул держит байк (id водителя); пусто — ничей. См. `lib/pool.ts`. */
  poolDriverId: z.string().default(''),
  /** RFC3339 UTC — когда попал в этот пул. */
  poolSince: z.string().default(''),
  /**
   * Кто держит in_pool/in_transit: "" | driver | collector (волна 3). Байк
   * сборщика водителю не выбрать, подпись — по держателю (`heldByCollectorLabel`).
   */
  poolKind: z.string().default(''),
  /** RFC3339: когда водитель последний раз поставил этот байк (его пул); пусто — никогда. */
  poolDeliveredAt: z.string().default(''),
})
export type ScoutVehicle = z.infer<typeof scoutVehicleSchema>

/** Номер с наклейки. C1-поле, а до его выката — тот же qrToken. */
export function displayNumber(v: Pick<ScoutVehicle, 'number' | 'qrToken'>): string {
  return v.number || v.qrToken
}

const scoutVehiclesReply = z.object({
  vehicles: z.array(scoutVehicleSchema).default([]),
  nextPageToken: z.string().default(''),
})

/** Ниже — заряда не хватит на поездку. Владеет порогом `lib/describe-bike.ts`. */
export { LOW_BATTERY_PCT } from '@/lib/describe-bike'

export const VQK = {
  vehicles: ['scout.vehicles'] as const,
  rideZones: ['scout.rideZones'] as const,
}

/**
 * Парк одной страницей.
 *
 * Фильтров у ручки почти нет (ни гео, ни заряда, ни зоны), и это нормально:
 * единиц десятки, а фильтры скаута комбинируются и должны срабатывать мгновенно.
 */
/**
 * `refetchInterval` — только карта (§2.4: автообновление каждые 20 с, пока
 * экран открыт). Ключ запроса общий на всё приложение, поэтому пока карта
 * открыта, её фоновый опрос держит свежим один и тот же кэш и для списка, и
 * для хабов (`api/hubs.ts`), даже те их вызовы, что интервал не задают.
 */
export function useScoutVehicles(pageSize = 200, opts: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [...VQK.vehicles, pageSize],
    queryFn: () =>
      request('/v1/scout/vehicles', scoutVehiclesReply, { query: { pageSize } }),
    staleTime: 30_000,
    ...opts,
  })
}

/**
 * Зоны катания — все сразу, без bbox.
 *
 * `ListInBBox` при нулевом bbox отдаёт всё без фильтра
 * (`fleet/internal/data/ride_zone_repo.go`), поэтому привязки к вьюпорту нет.
 * Это важнее, чем экономия трафика: вердикт «за зоной» по набору зон, собранному
 * под камеру, врал бы про байки за краем экрана.
 *
 * Зоны меняются раз в месяц — держим их свежими час.
 */
const rideZonesReply = z.object({
  zones: z
    .array(
      z.object({
        id: z.string().default(''),
        name: z.string().default(''),
        kind: z.string().default(''),
        polygonWkt: z.string().default(''),
      }),
    )
    .default([]),
})

export function useRideZones() {
  return useQuery({
    queryKey: VQK.rideZones,
    queryFn: async (): Promise<RideZone[]> =>
      (await request('/v1/ride-zones', rideZonesReply)).zones,
    staleTime: 60 * 60_000,
  })
}

/* ─── Наклейка/номер → велосипед ──────────────────────────────────────── */

/**
 * Ответ открытой ручки `GET /v1/vehicles/qr/{token}/public`.
 *
 * Это PublicVehicle: без imei, зато с runtime_stale. Нам отсюда нужен только
 * `id`, чтобы уйти на карточку, — остальное возьмётся из списка парка.
 */
const publicVehicleByQr = z.object({
  vehicle: nullAsMissing(
    z
      .object({
        id: z.string().default(''),
        qrToken: z.string().default(''),
        status: z.string().default(''),
      })
      .default({ id: '', qrToken: '', status: '' }),
  ),
})

/**
 * Спросить сервер, что за велосипед под этим кодом наклейки (сканер).
 *
 * ⚠ НЕ `ListVehicles?qr_filter=`. Та ручка тоже ищет по наклейке и на первый
 * взгляд подходит, но она staff-only; скаутская `/v1/scout/vehicles` с тем же
 * фильтром вернёт 403 до выката бэкенда, а эта ручка открытая (pre-auth) и
 * работает уже сегодня.
 */
export async function lookupVehicleByQr(encodedToken: string): Promise<string> {
  const out = await request(`/v1/vehicles/qr/${encodedToken}/public`, publicVehicleByQr, {
    anonymous: true,
  })
  return out.vehicle.id
}

function toNumbered(vehicles: readonly ScoutVehicle[]): NumberedBike[] {
  return vehicles.map((v) => ({ id: v.id, number: displayNumber(v) }))
}

/**
 * `GET /v1/scout/vehicles/by-number/{n}` отвечает обёрткой `{ vehicle }` —
 * как `GET /v1/vehicles/{id}` в админке, а не голым ScoutVehicle.
 */
const byNumberReply = z.object({
  vehicle: nullAsMissing(scoutVehicleSchema.default({
    id: '',
    qrToken: '',
    number: '',
    vehicleType: '',
    status: '',
    batteryPct: 0,
    locked: false,
    lat: 0,
    lng: 0,
    lastSeenAt: '',
    problem: '',
    problemDetail: '',
    faultReason: '',
    lockedConfirmedAt: '',
    labels: [],
    poolDriverId: '',
    poolSince: '',
    poolDeliveredAt: '',
    poolKind: '',
  })),
})

export type ResolveByNumberResult =
  | { kind: 'found'; id: string }
  | { kind: 'not_found' }
  | { kind: 'ambiguous'; candidates: string[] }

/** `metadata.candidates` формы никто ещё не видел (C1 не выкачен) — читаем терпимо. */
function parseCandidates(metadata: Record<string, string>): string[] {
  const raw = metadata.candidates ?? ''
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.map(String)
  } catch {
    /* не JSON — значит список через запятую */
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
}

/**
 * Резолв номера в id байка — сердце экрана «Велик» (§2.1 спецификации).
 *
 * Сначала ЛОКАЛЬНО, по уже загруженному парку: офлайн и мгновенно, и это
 * главный случай — парк из полусотни байков весь помещается в кэш. Не нашли
 * (или нашли несколько — редкая, но реальная неоднозначность) — спрашиваем
 * сервер: `GET /v1/scout/vehicles/by-number/{n}` (C1) видит байков за
 * пределами первой страницы и умеет точный ответ 409 на неоднозначность.
 *
 * Маршрут ещё не везде выкачен: 404 без разбора reason (roundtrip до
 * несуществующего пути и honest "не нашли" неотличимы отсюда) — это тоже
 * «не нашли», а не сбой. Отдельного состояния «ждём выката» здесь нет:
 * локальный список уже покрывает прод-парк, и лишний экран честности ничего
 * не добавил бы к простому «Велика N нет в парке».
 */
export async function resolveBikeByNumber(
  input: string,
  cached: readonly ScoutVehicle[],
): Promise<ResolveByNumberResult> {
  const local = findByNumber(input, toNumbered(cached))
  if (local.length === 1) return { kind: 'found', id: local[0]!.id }
  if (local.length > 1) return { kind: 'ambiguous', candidates: local.map((b) => b.number) }

  try {
    // Ответ обёрнут в `{ vehicle: ... }` — как `GET /v1/vehicles/{id}` в
    // админке, а не голый ScoutVehicle (уточнение владельца контракта C1).
    const out = await request(
      `/v1/scout/vehicles/by-number/${encodeURIComponent(input.trim())}`,
      byNumberReply,
    )
    return { kind: 'found', id: out.vehicle.id }
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      return { kind: 'ambiguous', candidates: parseCandidates(e.metadata) }
    }
    // 404 (не нашли или маршрут не выкачен) и NetworkError (нет сети, а
    // локально тоже не нашли) — для скаута один и тот же честный исход.
    if (e instanceof ApiError || e instanceof NetworkError) return { kind: 'not_found' }
    throw e
  }
}

export function useResolveBikeByNumber() {
  return useMutation({
    mutationFn: ({ input, cached }: { input: string; cached: readonly ScoutVehicle[] }) =>
      resolveBikeByNumber(input, cached),
  })
}

/* ─── Смена статуса ───────────────────────────────────────────────────── */

/*
 * ⚠ ЛОГИКА СТАТУСОВ ЖИВЁТ В `@/lib/vehicle-status`, А НЕ ЗДЕСЬ, и это не
 * перестановка ради красоты.
 *
 * Тот же самый маршрут нужен офлайн-очереди (`lib/offline/outbox.ts`), а она
 * не может импортировать этот файл: он сам тянет из неё `enqueue` — получился
 * бы цикл. Реэкспорт — чтобы экраны не правили импорты; источник правды один.
 */
export {
  SCOUT_STATUS_ACTIONS,
  canScoutSet,
  statusActionLabel,
  statusActionPath,
  statusBlockedReason,
  vehicleStatusLabel,
  type ScoutStatusAction,
} from '@/lib/vehicle-status'

/**
 * Подменить статус одного байка в снимке парка.
 *
 * ⚠ ИМЯ ДЕЙСТВИЯ И ЕСТЬ НОВЫЙ СТАТУС, отдельной таблицы action→status не
 * существует и заводить её не нужно. Все шесть значений `ScoutStatusAction`
 * дословно совпадают со статусами транспорта; расходятся только URL ручек, и
 * ровно для них есть `ACTION_PATH` в `lib/vehicle-status.ts`. Вторая таблица
 * рядом с той — это будущее расхождение, про которое написана её шапка.
 *
 * Форма ответа проверяется, а не предполагается: в кэше может лежать
 * `undefined` (запрос ещё не ходил) или восстановленный из IndexedDB снимок
 * прошлой версии схемы. Оба случая — «оставить как есть», а не выдумать
 * запись.
 */
function patchVehicleStatus(old: unknown, vehicleId: string, status: string): unknown {
  if (typeof old !== 'object' || old === null) return old
  const list = (old as { vehicles?: unknown }).vehicles
  if (!Array.isArray(list)) return old
  return {
    ...old,
    vehicles: list.map((v) =>
      typeof v === 'object' && v !== null && (v as ScoutVehicle).id === vehicleId
        ? { ...(v as ScoutVehicle), status }
        : v,
    ),
  }
}

/**
 * Сменить статус байка.
 *
 * Через очередь, как и всё остальное: скаут стоит в паркинге, и «пометил
 * сломанным» обязано приниматься без сети. Второго пути к API для мутаций
 * не существует — правило CLAUDE.md.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ ПОЧЕМУ ЗДЕСЬ ОПТИМИСТИЧНЫЙ ПАТЧ, А НЕ ОДНА ИНВАЛИДАЦИЯ
 * ─────────────────────────────────────────────────────────────────────────
 * Жалоба скаутов: «Поставил здесь» долго думает. Одна `invalidateQueries`
 * перечитывает парк с сервера, а там ещё старый статус: запись только легла
 * в очередь, до сервера она доедет позже. Экран оставался прежним, и кнопку
 * жали второй раз.
 *
 * Патч ставится ДО отправки и откатывается, если очередь запись не приняла
 * (приватный режим, кончилась квота). Отказ САМОГО СЕРВЕРА сюда не приходит
 * вовсе — он приезжает асинхронно в `drainOutbox`, и правду там
 * восстанавливает инвалидация парка после досылки (`app/providers.tsx`).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ НА УСПЕХЕ ПАРК НЕ ПЕРЕЗАПРАШИВАЕТСЯ, И ЭТО НЕ ЗАБЫТАЯ СТРОКА
 * ─────────────────────────────────────────────────────────────────────────
 * Успех этой мутации — это «запись легла в очередь», а не «сервер согласен».
 * Инвалидация здесь перечитала бы парк в тот момент, когда сервер про смену
 * ещё не знает: досылка начинается с `await readOutbox()` (чтение IndexedDB)
 * и стартует ПОЗЖЕ, чем уходит рефетч по инвалидации. Карточка байка, где и
 * живут кнопки статуса, держит запрос парка смонтированным, поэтому рефетч
 * реально уходит, а не просто метит запрос устаревшим.
 *
 * Скаут в итоге видел новый статус → старый → снова новый. Это ровно тот
 * симптом, ради которого патч и написан, только отложенный на один круг.
 *
 * Правду о статусе приносят два уже существующих пути: инвалидация после
 * `drainOutbox` (там приговор сервера) и двадцатисекундный опрос карты.
 */
export function useSetVehicleStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      vehicleId,
      action,
      reason,
    }: {
      vehicleId: string
      action: ScoutStatusAction
      reason: string
    }) => enqueue('vehicle_status', vehicleId, { action, reason }),
    onMutate: async ({ vehicleId, action }) => {
      // Без отмены летящий запрос (карта опрашивает парк раз в 20 с)
      // приземлится поверх патча и вернёт старый статус на экран.
      await qc.cancelQueries({ queryKey: VQK.vehicles })
      const snapshot = qc.getQueriesData({ queryKey: VQK.vehicles })
      qc.setQueriesData<unknown>({ queryKey: VQK.vehicles }, (old: unknown) =>
        patchVehicleStatus(old, vehicleId, action),
      )
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data)
      // Снимок мог устареть, пока мутация шла. После отката перечитываем —
      // здесь это безопасно: ничего оптимистичного на экране уже не осталось.
      void qc.invalidateQueries({ queryKey: VQK.vehicles })
    },
  })
}

/* ─── Лейблы ──────────────────────────────────────────────────────────── */

const labelsReply = z.object({ labels: z.array(labelSchema).default([]) })

/** Справочник лейблов. Меняется редко (создаёт диспетчер) — держим час. */
export function useLabels() {
  return useQuery({
    queryKey: ['scout.labels'] as const,
    queryFn: async () => (await request('/v1/labels', labelsReply)).labels,
    staleTime: 60 * 60_000,
  })
}

function patchVehicleLabels(
  old: unknown,
  vehicleId: string,
  change: (labels: Label[]) => Label[],
): unknown {
  if (typeof old !== 'object' || old === null) return old
  const list = (old as { vehicles?: unknown }).vehicles
  if (!Array.isArray(list)) return old
  return {
    ...old,
    vehicles: list.map((v) =>
      typeof v === 'object' && v !== null && (v as ScoutVehicle).id === vehicleId
        ? { ...(v as ScoutVehicle), labels: change((v as ScoutVehicle).labels) }
        : v,
    ),
  }
}

/**
 * Общая обвязка привязки/отвязки: оптимистичный патч парка и откат, если
 * очередь запись не приняла. Причины, по которым парк на успехе не
 * перечитывается, — те же, что у `useSetVehicleStatus`.
 */
function useLabelMutation<V extends { vehicleId: string }>(
  enqueueFn: (vars: V) => Promise<unknown>,
  change: (vars: V) => (labels: Label[]) => Label[],
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: enqueueFn,
    onMutate: async (vars: V) => {
      await qc.cancelQueries({ queryKey: VQK.vehicles })
      const snapshot = qc.getQueriesData({ queryKey: VQK.vehicles })
      qc.setQueriesData<unknown>({ queryKey: VQK.vehicles }, (old: unknown) =>
        patchVehicleLabels(old, vars.vehicleId, change(vars)),
      )
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data)
      void qc.invalidateQueries({ queryKey: VQK.vehicles })
    },
  })
}

export function useAttachLabel() {
  return useLabelMutation<{ vehicleId: string; label: Label }>(
    ({ vehicleId, label }) => enqueue('label_attach', vehicleId, { labelId: label.id }),
    ({ label }) =>
      (labels) =>
        labels.some((l) => l.id === label.id) ? labels : [...labels, label],
  )
}

export function useDetachLabel() {
  return useLabelMutation<{ vehicleId: string; labelId: string }>(
    ({ vehicleId, labelId }) => enqueue('label_detach', vehicleId, { labelId }),
    ({ labelId }) =>
      (labels) =>
        labels.filter((l) => l.id !== labelId),
  )
}

/* ─── Замечание диспетчеру ────────────────────────────────────────────── */

/**
 * Категории заявки. Значения — серверный enum
 * (`fleet/ent/schema/work_order.go`, поле `category`).
 *
 * ⚠ ЭТО НЕ БЕЗОБИДНЫЙ КОММЕНТАРИЙ, И ПОРЯДОК ЗДЕСЬ НЕ АЛФАВИТНЫЙ.
 * `fleet/internal/biz/work_order.go` назначает `severity` сам:
 * `electrical` и `controller` дают `critical`, а критическая заявка на
 * доступном байке НЕМЕДЛЕННО переводит его в `faulty` и заводит задачу
 * «забрать». Ещё три любые открытые заявки на одном доступном байке дают
 * тот же результат (`autoFaultyReportThreshold = 3`).
 *
 * Поэтому «прочее» стоит первым и выбрано по умолчанию: это `minor`, и
 * скаут, которому надо просто оставить след, не снимет байк с линии,
 * не заметив этого.
 */
export const REPORT_CATEGORIES = [
  'other',
  'mechanical',
  'battery',
  'connectivity',
  'vandalism',
  'electrical',
  'controller',
] as const
export type ReportCategory = (typeof REPORT_CATEGORIES)[number]

export const reportCategoryLabel: Record<ReportCategory, string> = {
  other: 'Прочее',
  mechanical: 'Механика',
  battery: 'Батарея',
  connectivity: 'Связь',
  vandalism: 'Вандализм',
  electrical: 'Электрика',
  controller: 'Контроллер',
}

/** Категории, которые снимают байк с линии сразу. Зеркало `severity` в fleet. */
const CRITICAL_CATEGORIES = new Set<ReportCategory>(['electrical', 'controller'])

/**
 * Предупреждение под кнопкой. `null` — предупреждать не о чем.
 *
 * Существует затем, чтобы последствие называлось до нажатия, а не
 * обнаруживалось потом сменившимся статусом.
 */
export function reportConsequence(category: ReportCategory): string | null {
  if (!CRITICAL_CATEGORIES.has(category)) return null
  return 'Байк сразу уйдёт из линии в «Сломан», и на него заведётся задача «Забрать».'
}

/**
 * Оставить замечание по байку.
 *
 * Через очередь, как все мутации: замечание чаще всего пишут стоя у байка,
 * то есть там же, где нет сети.
 *
 * Ручка `POST /v1/vehicles/{id}/report` открыта любому авторизованному
 * (`public-api/internal/service/handler_ops.go`), поэтому 403 здесь не ждём.
 *
 * ⚠ Заявка приезжает диспетчеру с `source = "rider_report"`: значения
 * `scout_inspection` в enum нет — до тех пор скаут узнаётся по тексту заметки.
 */
export function useReportProblem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      vehicleId,
      category,
      note,
    }: {
      vehicleId: string
      category: ReportCategory
      note: string
    }) => enqueue('vehicle_report', vehicleId, { category, note }),
    onSuccess: () => {
      // Критическая заявка меняет статус байка на сервере — список парка
      // после досылки перестаёт быть правдой.
      void qc.invalidateQueries({ queryKey: VQK.vehicles })
    },
  })
}

/* ─── Сигнал и последняя поездка ──────────────────────────────────────── */

/**
 * Заставить байк пикнуть.
 *
 * ⚠ МИМО ОЧЕРЕДИ, как весь `api/device.ts`, и по той же причине: писк — это
 * живая радиокоманда, а не бизнес-состояние. Досланный через сорок минут, он
 * прозвучит во дворе, где скаута давно нет.
 */
export function useRingVehicle(vehicleId: string) {
  return useMutation({
    mutationFn: () =>
      request(`/v1/scout/vehicles/${vehicleId}/ring`, z.object({ status: z.string().default('') }), {
        method: 'POST',
        body: { id: vehicleId },
      }),
  })
}

/**
 * Где закончилась последняя поездка.
 *
 * Ответ намеренно куцый — точка и время, без личности клиента, стоимости и
 * трека (`ScoutVehicleLastDrop` в public.proto). `found: false` — это ответ
 * «на байке ещё не ездили» либо «поездка была, но следа GPS не осталось».
 */
const lastDropReply = z.object({
  found: z.boolean().default(false),
  endedAt: z.string().default(''),
  lat: z.number().default(0),
  lng: z.number().default(0),
})
export type LastDrop = z.infer<typeof lastDropReply>

export function useLastDrop(vehicleId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['scout.lastDrop', vehicleId],
    queryFn: () => request(`/v1/scout/vehicles/${vehicleId}/last-drop`, lastDropReply),
    enabled: enabled && vehicleId !== '',
    staleTime: 5 * 60_000,
    retry: false,
  })
}
